import React, { Fragment, memo, useState } from 'react';
import { styled } from 'styled-components';
import { Colors, hexToRGB } from '../../../../../play-core/styles/Colors';
import { useAnalyticsDispatch } from '../../../analytics/AnalyticsContext';
import { useUdpSectionTracking } from '../../../analytics/use-udp-section-tracking';
import { usePath } from '../../../config/VideoAppContext';
import { useDataDispatch, useShowState } from '../../../providers/DataProvider/DataContext';
import { SmartLink } from '../../ClickableComponents/SmartLink';
import { posterImageWidth, teaserImageWidth } from '../../Image/image-width';
import { PlayImage, aspectRatioMap } from '../../Image/PlayImage';
import { useSectionMeta } from '../../Section/SectionMetaProvider';
import { ShowImageOverlay } from '../TeaserActions/ShowImageOverlay';
import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
export const ShowTeaserWrapper = /*#__PURE__*/memo(({
  children,
  show,
  index,
  linkStyles,
  ariaLabel,
  aspectRatio = '16/9',
  imageWidth = aspectRatio === '2/3' ? posterImageWidth : teaserImageWidth,
  smallImageWidth = aspectRatio === '2/3' ? posterImageWidth : teaserImageWidth,
  mediumImageWidth = aspectRatio === '2/3' ? posterImageWidth : teaserImageWidth,
  largeImageWidth = mediumImageWidth,
  xLargeImageWidth = aspectRatio === '2/3' ? posterImageWidth : teaserImageWidth,
  onlyFavor = false,
  hasBorderRadius = true,
  showFavoriteAction,
  isHoveringParent = false,
  isFocusedParent = false,
  onClick = () => {},
  ...rest
}) => {
  const {
    videoShowPath
  } = usePath();
  const [isHovering, setHover] = useState(false);
  const [isFocused, setFocus] = useState(false);
  const {
    favoriteShowIds
  } = useShowState();
  const sectionMeta = useSectionMeta();
  const {
    sectionId,
    representationName,
    sectionTitle
  } = sectionMeta;
  const analytics = useAnalyticsDispatch();
  const dispatch = useDataDispatch();
  const isFavorite = favoriteShowIds.includes(show.id);
  const trackSection = useUdpSectionTracking();
  const imageUrl = aspectRatio === '2/3' && show.posterImageUrl || show.imageUrl;
  const onFavorite = () => {
    dispatch({
      type: 'toggle-show-favorite',
      showId: show.id,
      showTitle: show.title,
      showUndo: rest.showFavoriteUndo,
      showSnackbar: rest.showFavoriteSnackbar
    });
  };
  const TeaserImage = /*#__PURE__*/_jsx(PlayImage, {
    src: imageUrl,
    alt: show.title,
    width: imageWidth,
    smallImageWidth: smallImageWidth,
    mediumImageWidth: mediumImageWidth,
    largeImageWidth: largeImageWidth,
    xLargeImageWidth: xLargeImageWidth,
    aspectRatio: aspectRatio,
    hasBorderRadius: hasBorderRadius
  });
  return /*#__PURE__*/_jsxs(Layout, {
    onMouseEnter: () => setHover(true),
    onMouseLeave: () => setHover(false),
    onFocus: () => setFocus(true),
    onBlur: () => setFocus(false),
    "data-testclass": "show-teaser",
    children: [onlyFavor ? /*#__PURE__*/_jsx(Clickable, {
      tabIndex: 0,
      "aria-label": show.title,
      onClick: () => {
        onClick();
        onFavorite();
      },
      children: TeaserImage
    }) : /*#__PURE__*/_jsxs(SmartLink, {
      to: videoShowPath(show),
      "aria-label": show.title,
      isInternal: true,
      state: {
        show
      },
      onClick: () => {
        onClick();
        if (sectionId || representationName) {
          analytics({
            type: 'click-event',
            name: 'ShowTeaserClick',
            value1: show.urn,
            value2: sectionId,
            value3: sectionTitle,
            value4: representationName
          });
          trackSection({
            sectionMeta,
            item: {
              ...show,
              index
            }
          });
        }
      },
      children: [TeaserImage, /*#__PURE__*/_jsx(Fragment, {
        children: children
      })]
    }), onlyFavor && isFavorite && /*#__PURE__*/_jsx(ImageShadow, {}), /*#__PURE__*/_jsx(ShowImageOverlay, {
      aspectRatio: aspectRatioMap[aspectRatio],
      isHovering: isHovering || isHoveringParent,
      isFocused: isFocused || isFocusedParent,
      show: show,
      showFavoriteAction: showFavoriteAction,
      ...rest
    })]
  });
});
const Layout = styled.section.withConfig({
  displayName: "ShowTeaserWrapper__Layout",
  componentId: "sc-1rxfvyz-0"
})(["display:flex;flex-direction:column;position:relative;"]);
const Clickable = styled.div.withConfig({
  displayName: "ShowTeaserWrapper__Clickable",
  componentId: "sc-1rxfvyz-1"
})(["cursor:pointer;"]);
const ImageShadow = styled.div.withConfig({
  displayName: "ShowTeaserWrapper__ImageShadow",
  componentId: "sc-1rxfvyz-2"
})(["position:absolute;top:0;left:0;width:100%;height:100%;pointer-events:none;overflow:hidden;background:", ";"], hexToRGB(Colors.GREY_1a1a1a, 0.7));